/* eslint-disable @typescript-eslint/naming-convention */
import { AnalyticsContextValue } from "@swiggy-private/checkout-core";

import { SCREEN_NAME, CLICK_ACTIONS, IMPRESSION_ACTIONS } from "../constants";

export interface OnPremiseFulfilmentClickPayload {
    restaurantId: string;
    cartId: string;
}

export interface OnPremiseEditOrderTypeClickPayload extends OnPremiseFulfilmentClickPayload {
    currentSeatNum: string;
    currentScreen: string;
}

export interface OnPremiseHalfCardConfirmContext extends OnPremiseFulfilmentClickPayload {
    orderType: string;
    seatNum?: string;
    screen?: string;
}

export const ON_PREMISE_HALFCARD_OBJECT_VALUE = {
    DELIVER: "deliver",
    PICKUP: "pick-up",
    CONFIRM: "confirm",
    UPDATE: "update",
    MOBILE_NUMBER: "mobile-number",
    CLOSE: "close",
} as const;

const buildBaseContext = ({ restaurantId, cartId }: OnPremiseFulfilmentClickPayload): string =>
    JSON.stringify({ restaurant_id: restaurantId, cart_id: cartId });

const buildConfirmContext = ({
    restaurantId,
    cartId,
    orderType,
    seatNum,
    screen,
}: OnPremiseHalfCardConfirmContext): string => {
    const context: Record<string, string> = {
        restaurant_id: restaurantId,
        cart_id: cartId,
        order_type: orderType,
    };
    if (seatNum !== undefined) {
        context.seat_num = seatNum;
    }
    if (screen !== undefined) {
        context.screen = screen;
    }
    return JSON.stringify(context);
};

export const clickSelectOrderType = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    { restaurantId, cartId }: OnPremiseFulfilmentClickPayload,
): void => {
    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: CLICK_ACTIONS.SELECT_ORDER_TYPE,
        context: buildBaseContext({ restaurantId, cartId }),
    });
};

export const clickEditOrderType = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    { restaurantId, cartId, currentSeatNum, currentScreen }: OnPremiseEditOrderTypeClickPayload,
): void => {
    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: CLICK_ACTIONS.EDIT_ORDER_TYPE,
        context: JSON.stringify({
            restaurant_id: restaurantId,
            cart_id: cartId,
            current_seat_num: currentSeatNum,
            current_screen: currentScreen,
        }),
    });
};

export const clickEditMobileNumber = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    { restaurantId, cartId }: OnPremiseFulfilmentClickPayload,
): void => {
    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: CLICK_ACTIONS.EDIT_MOBILE_NUMBER,
        context: buildBaseContext({ restaurantId, cartId }),
    });
};

// --- Order type half card ---

export const impressionOrderTypeHalfCard = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    payload: OnPremiseFulfilmentClickPayload,
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_ORDER_TYPE_HALFCARD,
        context: buildBaseContext(payload),
    });
};

const clickOrderTypeHalfCard = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    payload: OnPremiseFulfilmentClickPayload,
    objectValue: string,
    context?: string,
): void => {
    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: CLICK_ACTIONS.ORDER_TYPE_HALFCARD,
        label: objectValue,
        context: context ?? buildBaseContext(payload),
    });
};

export const clickOrderTypeHalfCardDeliver = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    payload: OnPremiseFulfilmentClickPayload,
): void => {
    clickOrderTypeHalfCard(clickEvent, payload, ON_PREMISE_HALFCARD_OBJECT_VALUE.DELIVER);
};

export const clickOrderTypeHalfCardPickup = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    payload: OnPremiseFulfilmentClickPayload,
): void => {
    clickOrderTypeHalfCard(clickEvent, payload, ON_PREMISE_HALFCARD_OBJECT_VALUE.PICKUP);
};

export const clickOrderTypeHalfCardMobileNumber = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    payload: OnPremiseFulfilmentClickPayload,
): void => {
    clickOrderTypeHalfCard(clickEvent, payload, ON_PREMISE_HALFCARD_OBJECT_VALUE.MOBILE_NUMBER);
};

export const clickOrderTypeHalfCardClose = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    payload: OnPremiseFulfilmentClickPayload,
): void => {
    clickOrderTypeHalfCard(clickEvent, payload, ON_PREMISE_HALFCARD_OBJECT_VALUE.CLOSE);
};

export const clickOrderTypeHalfCardConfirmOrUpdate = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    confirmContext: OnPremiseHalfCardConfirmContext,
    isUpdate: boolean,
): void => {
    const { restaurantId, cartId, orderType, seatNum, screen } = confirmContext;
    clickOrderTypeHalfCard(
        clickEvent,
        { restaurantId, cartId },
        isUpdate ? ON_PREMISE_HALFCARD_OBJECT_VALUE.UPDATE : ON_PREMISE_HALFCARD_OBJECT_VALUE.CONFIRM,
        buildConfirmContext({ restaurantId, cartId, orderType, seatNum, screen }),
    );
};
